/**
 * supabaseFeatureFlags.ts — Lese/Schreib-Schicht für `feature_flags`.
 *
 * Sicherheits-Garantien (siehe Migration 0024):
 *   - SELECT für alle authenticated User (RLS).
 *   - INSERT/UPDATE/DELETE nur für Admin (RLS).
 *   - key (PK) ist immutable (Trigger).
 *   - updated_by wird DB-seitig auf auth.uid() gesetzt (Trigger).
 *   - Audit-Log via Trigger.
 *
 * Nutzung im UI:
 *   - getFeatureFlag('llm_suggestions') liefert true/false.
 *   - Im LLM-Pfad zusätzlich isMistralConfigured() checken (Doppel-Gate).
 *   - Für die Admin-Settings-Page: listAllFeatureFlags() + setFeatureFlag().
 */

import { getSupabaseClient } from './supabase';
import type { FeatureFlagRow } from './supabaseTypes';

// Hand-getypter Cast für die SELECT-Resultate. Der globale Database-Type
// im Workspace ist absichtlich unvollständig (siehe supabaseTypes.ts), daher
// fallen die SELECT-Inferenz und supabase-js zurück auf `never`. Wir kapseln
// den Cast hier, damit Aufrufer-Code typsicher bleibt.
type FlagSelectRow = Pick<FeatureFlagRow, 'enabled'>;

const FLAG_KEY_RE = /^[a-z][a-z0-9_]{2,63}$/;

/** Test-Hook: Cache leeren. Nicht in Production-Code aufrufen. */
const flagCache: Map<string, { value: boolean; ts: number }> = new Map();
const CACHE_TTL_MS = 30_000; // 30 s — Toggle-Wechsel propagieren spätestens nach 30 s

export interface FeatureFlag {
  readonly key: string;
  readonly enabled: boolean;
  readonly description: string;
  readonly updatedAt: Date;
  readonly updatedBy: string | null;
}

function rowToFlag(row: FeatureFlagRow): FeatureFlag {
  return {
    key: row.key,
    enabled: row.enabled,
    description: row.description,
    updatedAt: new Date(row.updated_at),
    updatedBy: row.updated_by,
  };
}

/**
 * Liest einen einzelnen Flag. Default false wenn nicht gefunden.
 *
 * Cached für 30 s, damit nicht jeder Render-Cycle einen DB-Roundtrip macht.
 * Cache wird durch setFeatureFlag() invalidiert.
 *
 * @throws Error nur bei echten DB-Fehlern (nicht bei „Flag nicht vorhanden").
 */
export async function getFeatureFlag(key: string): Promise<boolean> {
  if (!FLAG_KEY_RE.test(key)) {
    throw new Error(`getFeatureFlag: ungültiger Key "${key}" — erwartet [a-z][a-z0-9_]{2,63}`);
  }
  const now = Date.now();
  const cached = flagCache.get(key);
  if (cached && now - cached.ts < CACHE_TTL_MS) {
    return cached.value;
  }

  const sb = getSupabaseClient();
  const { data, error } = await sb.from('feature_flags').select('*').eq('key', key).maybeSingle();

  if (error) {
    throw new Error(`getFeatureFlag [${error.code ?? '??'}]: ${error.message}`);
  }
  const row = data as FlagSelectRow | null;
  const value = row?.enabled === true;
  flagCache.set(key, { value, ts: now });
  return value;
}

/**
 * Liest alle Flags — für Admin-Settings-Page. Nicht gecached, weil die
 * Admin-Sicht aktuelle Werte braucht.
 */
export async function listAllFeatureFlags(): Promise<FeatureFlag[]> {
  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('feature_flags')
    .select('*')
    .order('key', { ascending: true });

  if (error) {
    throw new Error(`listAllFeatureFlags [${error.code ?? '??'}]: ${error.message}`);
  }
  const rows = (data ?? []) as ReadonlyArray<FeatureFlagRow>;
  return rows.map(rowToFlag);
}

/**
 * Admin-Action: schaltet Flag um. RLS verbietet das für Nicht-Admins, der
 * Trigger setzt updated_by auf auth.uid().
 *
 * @throws Error wenn User kein Admin ist oder DB-Fehler auftritt.
 */
export async function setFeatureFlag(key: string, enabled: boolean): Promise<void> {
  if (!FLAG_KEY_RE.test(key)) {
    throw new Error(`setFeatureFlag: ungültiger Key "${key}"`);
  }
  const sb = getSupabaseClient();
  const { error } = await sb
    .from('feature_flags')
    .update({ enabled } as never)
    .eq('key', key);

  if (error) {
    throw new Error(`setFeatureFlag [${error.code ?? '??'}]: ${error.message}`);
  }
  // Cache invalidieren, damit der nächste getFeatureFlag-Call frisch lädt.
  flagCache.delete(key);
}

/**
 * Test-Hook: Cache komplett leeren. Wird in unit tests genutzt, damit
 * sequentielle Tests sich nicht gegenseitig beeinflussen.
 */
export function clearFeatureFlagCacheForTest(): void {
  flagCache.clear();
}
